import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { faqData } from '../../data/mock';
import { HelpCircle, Mail, Diamond } from 'lucide-react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const FAQ = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  
  return (
    <section id="faq" ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0B09] to-[#1A1512]" />
      
      {/* Diagonal Pattern */}
      <div className="absolute inset-0 diagonal-pattern opacity-20" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 opacity-10 hidden lg:block">
        <HelpCircle className="w-24 h-24 text-[#C9A97B]" />
      </div>
      <div className="absolute bottom-20 right-10 opacity-10 hidden lg:block">
        <HelpCircle className="w-16 h-16 text-[#C9A97B]" />
      </div>

      {/* Vertical decorative lines */}
      <div className="absolute left-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-[#C9A97B]/10 to-transparent hidden xl:block" />
      <div className="absolute right-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-[#C9A97B]/10 to-transparent hidden xl:block" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-blurIn' : 'opacity-0'}`}>
          <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 border border-[#C9A97B]/20 ${isVisible ? 'animate-rotateIn' : 'opacity-0'}`} style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
            <HelpCircle className="w-4 h-4" />
            Sıkça Sorulan Sorular
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Merak <span className="text-gold-gradient">Ettikleriniz</span>
          </h2>
          
          {/* Decorative line */}
          <div className={`flex items-center justify-center gap-3 mb-4 ${isVisible ? 'animate-expandWidth' : 'opacity-0'}`} style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#C9A97B]/40" />
            <Diamond className="w-3 h-3 text-[#C9A97B]/40" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#C9A97B]/40" />
          </div>
          
          <p className="text-[#BFB5A5] text-lg">
            En sık sorulan soruları sizin için derledik.
          </p>
        </div>

        {/* FAQ Accordion with enhanced styling */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqData.map((faq, index) => {
            // Alternatif yönlerden gelen animasyonlar
            const animationClass = index % 2 === 0 ? 'animate-slideInLeft' : 'animate-slideInRight';
            return (
              <div 
                key={faq.id}
                className={isVisible ? animationClass : 'opacity-0'}
                style={{ animationDelay: `${index * 120 + 400}ms`, animationFillMode: 'forwards' }}
              >
                <AccordionItem
                  value={`item-${faq.id}`}
                  className="group relative bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-xl border border-[#C9A97B]/10 px-6 overflow-hidden data-[state=open]:border-[#C9A97B]/30 transition-all duration-300 hover:border-[#C9A97B]/20"
                >
                {/* Number indicator */}
                <div className="absolute top-5 left-0 w-1 h-6 bg-gradient-to-b from-[#C9A97B]/50 to-transparent rounded-full opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity" />
                
                {/* Question number badge */}
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#251E18] border border-[#C9A97B]/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex">
                  <span className="text-xs text-[#C9A97B]">{index + 1}</span>
                </div>
                
                <AccordionTrigger className="text-left py-5 text-[#F5F0E8] font-medium hover:text-[#C9A97B] hover:no-underline transition-colors [&[data-state=open]]:text-[#C9A97B] group-hover:pl-2 duration-300">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#BFB5A5] pb-5 leading-relaxed">
                  <div className="pl-0 group-hover:pl-2 transition-all duration-300">
                    {/* Decorative quote mark */}
                    <span className="text-[#C9A97B]/20 text-4xl font-display leading-none float-left mr-2">"</span>
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          );
          })}
        </Accordion>

        {/* Additional Question with enhanced styling */}
        <div className={`mt-12 relative ${isVisible ? 'animate-scaleIn' : 'opacity-0'}`} style={{ animationDelay: '900ms', animationFillMode: 'forwards' }}>
          {/* Connecting line */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-[#C9A97B]/20 to-transparent" />
          
          <div className="text-center p-6 rounded-xl bg-gradient-to-br from-[#1F1914]/50 to-[#1A1512]/50 border border-[#C9A97B]/10">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#C9A97B]/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#C9A97B]" />
            </div>
            <p className="text-[#7A7067] mb-2">Başka sorularınız mı var?</p>
            <a
              href="mailto:info@tamayar.com"
              className="inline-flex items-center gap-2 text-[#C9A97B] hover:text-[#D4BC94] font-medium transition-colors group"
            >
              <span>info@tamayar.com adresinden bize ulaşın</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
